import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button, Copied1Icon, Copy2Icon } from '@blade/ui-kit'
import { useNetworkLinks } from '@root/entities/network'
import { walletsModelAtom } from '@root/entities/wallet/atoms'
import { StringUtil, useClipboard } from '@root/shared'
import { EyeIcon, Truncate } from '@root/shared/ui-kit'

import { TransactionListItem } from '../transaction-list-item'

import { TransactionDetailsRow } from './transaction-details-row'

import type { Transaction } from '@root/entities/transaction'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  tx: Transaction.Transaction
} & ClassNameProps

export const TransactionDetails: React.FC<Props> = ({ tx, className }) => {
  const { t } = useTranslation()
  const [isCopied, setCopied] = useState(false)

  const { copyToClipboard } = useClipboard()
  const { openTransactionExplorerLink } = useNetworkLinks()

  const [wallet] = useAtom(
    (ctx) => {
      const model = ctx.spy(walletsModelAtom)
      const wallets = ctx.spy(model.wallets)
      const wallet = wallets.find(
        (it) =>
          it.address === tx.swapInfo?.from ||
          it.address === tx.transferInfo?.from,
      )
      if (!wallet) {
        return undefined
      }
      const name = ctx.spy(wallet.name)
      return {
        name,
        address: wallet.address,
      }
    },
    [tx.swapInfo?.from, tx.transferInfo?.from],
  )

  const handleOpenInExplorer = () => {
    openTransactionExplorerLink(tx.hash)
  }

  const handleCopy = async () => {
    const isCopied = await copyToClipboard(tx.hash, {
      showSnackbar: true,
      snackbarText: t('transaction.transaction_hash_copied_message', {
        hash: StringUtil.truncateMiddle(tx.hash, 8, 9),
      }),
    })

    if (isCopied) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1000)
    }
  }

  const IconCopy = isCopied ? Copied1Icon : Copy2Icon

  return (
    <Root className={className}>
      <ListItem tx={tx} />
      <TransactionDetailsRow
        valueStart={
          wallet?.address ? (
            <Truncate start={4} end={5}>
              {wallet.address}
            </Truncate>
          ) : (
            '-'
          )
        }
        valueEnd={
          <Truncate start={8} end={9}>
            {tx.hash}
          </Truncate>
        }
        labelStart={wallet?.name ?? '-'}
        labelEnd={t('common.hash')}
        css={css`
          border-top: none;
        `}
      />
      <TransactionDetailsRow
        valueStart={tx.block}
        valueEnd={tx.nonce || '-'}
        labelStart={t('transaction.block_number')}
        labelEnd={t('transaction.nonce')}
      />
      <Actions>
        <Button
          radius={'rounded'}
          variant="secondary"
          size="s"
          iconLeft={<IconCopy />}
          onClick={handleCopy}
        />
        <ButtonExplore
          onClick={handleOpenInExplorer}
          fullWidth
          iconLeft={<EyeIcon />}
          size="s"
          color="primary"
          radius={'rounded'}
        >
          {t('transaction.see_in_explorer')}
        </ButtonExplore>
      </Actions>
    </Root>
  )
}

const ListItem = styled(TransactionListItem)`
  --tx-background-color: var(--background-2);
  --tx-background-color-hover: var(--background-2);
  --tx-separator-color: var(--backgroundA-2);
  --tx-separator-color-hover: var(--backgroundA-3);
  cursor: default;
`

const Root = styled.div`
  background: var(--background-1);
  padding-inline: 16px;
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 8px;

  padding-top: 8px;
  margin-bottom: 16px;
`

const ButtonExplore = styled(Button)`
  width: 100%;
`
